import { takeLatest, put, call } from 'redux-saga/effects';
import { initialize } from 'redux-form';
import _sortBy from 'lodash/sortBy';
import _groupBy from 'lodash/groupBy';
import _filter from 'lodash/filter';

import * as act from '../../actions';
import {
  EMPLOYEE_ROLE_PAGE_REQUEST,
  EMPLOYEE_ROLE_ON_CLICK_ROW,
  EMPLOYEE_ROLE_ON_CLICK_NEW_ROLE,
  EMPLOYEE_ROLE_SUBMIT_FORM_UPDATE,
  EMPLOYEE_ROLE_SUBMIT_FORM_CREATE,
  EMPLOYEE_ROLE_DELETE,
} from '../../actions/employees/role/constants';
import * as actNotif from '../../actions/notifications';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';
import * as actRole from '../../actions/employees/role';

import * as apiRole from '../../api/employees/role';
import * as apiEmployee from '../../api/employees';
import {
  assertErrorHasMessage,
  assertErrorHasMessageAndJson,
} from '../../utils/assert';
import { getAdvancedInventorySettings } from '../inventory/transfer';

export function* pageRequestFlow(request) {
  const {
    payload: { userId },
  } = request;
  yield put(actRole.closeRightColumn());
  yield put(showPageLoadingSpinner(true));
  yield put(actRole.setError(null));
  yield put(act.showProgressBar());
  try {
    const { data } = yield call(apiRole.getRoles);
    const roles = _sortBy(data.roles, (e) => e.name.toLowerCase());
    yield put(actRole.setRoles(roles));
    const {
      data: { permissions },
    } = yield call(apiEmployee.getPermission, {
      userId,
      params: { usable: true },
    });
    yield put(actRole.setAssignablePermissions(permissions));
    yield call(getAdvancedInventorySettings);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function initFormRole(role) {
  const permissionsOf = _groupBy(role.permissions, 'access');

  let activePermissionsBackoffice = permissionsOf['Back-office'] || [];
  if (permissionsOf.Endpoint) {
    activePermissionsBackoffice = activePermissionsBackoffice.concat(
      permissionsOf.Endpoint
    );
  }

  return initialize('role', {
    name: role.name,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    active_permissions_app: permissionsOf.App,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    active_permissions_backoffice: activePermissionsBackoffice,
  });
}

export function* onClickRow(request) {
  const {
    payload: { role },
  } = request;

  yield put(initFormRole(role));
  yield put(actRole.setError(null));
  yield put(actRole.setRoleDetail(role));
  yield put(actRole.openRightColumn());
}

export function* updateRoleFlow(request) {
  yield put(act.showProgressBar());
  yield put(actRole.isSubmittingForm(true));
  try {
    const {
      previousRoles,
      data: { roleId, ...params },
    } = request.payload;
    const { data } = yield call(apiRole.updateRole, roleId, params);
    yield put(actRole.setRoleDetail(data));

    const roles = previousRoles.map((role) =>
      role.id === roleId ? data : role
    );
    yield put(actRole.setRoles(roles));
    yield put(initFormRole(data));
    yield put(actNotif.success('Role was successfully saved.'));
  } catch (error) {
    assertErrorHasMessageAndJson(error);
    yield put(actRole.setError(error.jsonError));
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(actRole.isSubmittingForm(false));
  }
}

export function* createRoleFlow(request) {
  yield put(act.showProgressBar());
  yield put(actRole.isSubmittingForm(true));
  try {
    const { previousRoles } = request.payload;
    const { data } = yield call(apiRole.createRole, request.payload.data);
    yield put(actRole.setRoleDetail(data));

    const roles = [data].concat(previousRoles);
    yield put(actRole.setRoles(roles));
    yield put(initFormRole(data));
    yield put(actNotif.success('Role was successfully created.'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(actRole.isSubmittingForm(false));
  }
}

export function* deleteRoleFlow(request) {
  const {
    payload: { roleId, previousRoles },
  } = request;
  yield put(act.showProgressBar());
  try {
    yield call(apiRole.deleteRole, roleId);

    const roles = _filter(previousRoles, (role) => role.id !== roleId);
    yield put(actRole.setRoles(roles));
    yield put(actRole.setRoleDetail(null));

    yield put(actRole.closeRightColumn());
    yield put(actNotif.success('Role was successfully deleted.'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onClickNewRole(request) {
  yield put(actRole.openRightColumn());
  yield put(actRole.setRoleDetail(null));

  const { data } = request.payload;
  yield put(
    initialize('role', {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      active_permissions_app: data.activePermissionApp,
    })
  );
}

export default function* saga() {
  yield takeLatest(EMPLOYEE_ROLE_ON_CLICK_ROW, onClickRow);
  yield takeLatest(EMPLOYEE_ROLE_ON_CLICK_NEW_ROLE, onClickNewRole);
  yield takeLatest(EMPLOYEE_ROLE_PAGE_REQUEST, pageRequestFlow);
  yield takeLatest(EMPLOYEE_ROLE_SUBMIT_FORM_UPDATE, updateRoleFlow);
  yield takeLatest(EMPLOYEE_ROLE_SUBMIT_FORM_CREATE, createRoleFlow);
  yield takeLatest(EMPLOYEE_ROLE_DELETE, deleteRoleFlow);
}
